'use client'

import React, { useEffect, useRef, useState } from 'react'
import { Flex, SimpleGrid, Stack, Text } from '@mantine/core'
import { motion } from 'framer-motion'
import { IconArrowRight } from '@tabler/icons-react'
import Link from 'next/link'
import { BrowseHoverCategory } from '@/types/enums/StoreTypes'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { useBookVisitDetailHref } from '@/hooks/useBookVisitHref'
import { usePrescriptionRenewalStorefrontNavigation } from '@/hooks/usePrescriptionRenewalStorefrontNavigation'
import { BOOK_VISIT_DETAIL_HREF } from '@/lib/routes/booking'
import { PRESCRIPTION_RENEWAL_HREF } from '@/types/enums/sku-constants'
import {
    browseDropdownContainerVariants,
    browseDropdownColumnVariants,
    browseDropdownItemVariants,
} from '@/lib/animations'

interface BrowseDropdownContentProps {
    onClose: () => void
    onItemClick: (slug: string) => void
    isOpen?: boolean
    loggedIn: boolean
}

const BrowseDropdownContent: React.FC<BrowseDropdownContentProps> = ({
    onClose,
    onItemClick,
    isOpen = true,
    loggedIn,
}) => {
    const { isLg, isXl } = useBreakpoint()
    const contentRef = useRef<HTMLDivElement>(null)
    const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined)
    const { href: prescriptionRenewalHref, isNavigating, navigateToPrescriptionRenewal } =
        usePrescriptionRenewalStorefrontNavigation(loggedIn)
    const bookVisitDetailHref = useBookVisitDetailHref()

    useEffect(() => {
        if (!isOpen || !contentRef.current) {
            setMaxHeight(undefined)
            return
        }

        const checkScrollNeeded = () => {
            const contentElement = contentRef.current
            if (!contentElement) return
            const rect = contentElement.getBoundingClientRect()
            const dropdownTop = rect.top
            const viewportHeight = window.innerHeight
            const availableHeight = viewportHeight - dropdownTop
            const padding = 20
            const calculatedMaxHeight = availableHeight - padding

            const contentHeight = contentElement.scrollHeight
            if (contentHeight > calculatedMaxHeight && calculatedMaxHeight > 0) {
                setMaxHeight(calculatedMaxHeight)
            } else {
                setMaxHeight(undefined)
            }
        }

        checkScrollNeeded()
        window.addEventListener('resize', checkScrollNeeded)

        const timeoutId = setTimeout(checkScrollNeeded, 100)

        return () => {
            window.removeEventListener('resize', checkScrollNeeded)
            clearTimeout(timeoutId)
        }
    }, [isOpen])

    return (
        <motion.div
            ref={contentRef}
            variants={browseDropdownContainerVariants}
            initial="hidden"
            animate={isOpen ? 'visible' : 'hidden'}
            style={{
                maxHeight: maxHeight ? `${maxHeight}px` : undefined,
                overflowY: maxHeight ? 'auto' : 'visible',
            }}
        >
            <SimpleGrid cols={5} spacing={10} p={24} pr={32}>
                {BrowseHoverCategory.columns.map((column, columnIndex) => (
                    <motion.div key={column.name} variants={browseDropdownColumnVariants}>
                        <Stack gap={16}>
                            <Text fz={12} fw={650} lh="100%" c="#4F4E4D">
                                {column.name}
                            </Text>
                            {column.items.map((item, itemIndex) => {
                                const isPrescriptionRenewal = item.href === PRESCRIPTION_RENEWAL_HREF
                                const isBookVisit = item.href === BOOK_VISIT_DETAIL_HREF
                                return (
                                    <motion.div
                                        key={item.slug}
                                        variants={browseDropdownItemVariants}
                                        custom={itemIndex}
                                    >
                                        <Link
                                            href={
                                                isPrescriptionRenewal
                                                    ? prescriptionRenewalHref
                                                    : isBookVisit
                                                      ? bookVisitDetailHref
                                                      : item.href
                                            }
                                            aria-busy={
                                                isPrescriptionRenewal && isNavigating ? true : undefined
                                            }
                                            onClick={async (event) => {
                                                if (isPrescriptionRenewal) {
                                                    await navigateToPrescriptionRenewal(event)
                                                }
                                                onClose()
                                                onItemClick(item.slug)
                                            }}
                                            style={{ textDecoration: 'none' }}
                                        >
                                            <Flex
                                                align="center"
                                                gap="xs"
                                                style={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                }}
                                                onMouseEnter={(e) => {
                                                    const text = e.currentTarget.querySelector('span')
                                                    const icon = e.currentTarget.querySelector('svg')
                                                    if (text) text.style.color = '#2D1AFF'
                                                    if (icon) {
                                                        icon.style.opacity = '1'
                                                        icon.style.transform = 'translateX(0)'
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    const text = e.currentTarget.querySelector('span')
                                                    const icon = e.currentTarget.querySelector('svg')
                                                    if (text) text.style.color = 'var(--Foreground-fg-primary, #1F1E1D)'
                                                    if (icon) {
                                                        icon.style.opacity = '0'
                                                        icon.style.transform = 'translateX(-4px)'
                                                    }
                                                }}
                                            >
                                                <Text
                                                    component="span"
                                                    c="var(--Foreground-fg-primary, #1F1E1D)"
                                                    fz={isLg || isXl ? 24 : 16}
                                                    fw={400}
                                                    style={{
                                                        lineHeight: '130%',
                                                        letterSpacing: isLg || isXl ? '-0.48px' : '-0.32px',
                                                        transition: 'color 0.2s ease',
                                                        display: 'block',
                                                    }}
                                                >
                                                    {item.name}
                                                </Text>
                                                <IconArrowRight
                                                    size={isLg || isXl ? 20 : 16}
                                                    color="#2D1AFF"
                                                    style={{
                                                        opacity: 0,
                                                        transform: 'translateX(-4px)',
                                                        transition: 'all 0.2s ease',
                                                        marginTop: isLg || isXl ? '2px' : '1px',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            </Flex>
                                        </Link>
                                    </motion.div>
                                )
                            })}
                        </Stack>
                    </motion.div>
                ))}
            </SimpleGrid>
        </motion.div>
    )
}

export default BrowseDropdownContent
